/**
 * 字幕工具函数
 * 参考旧项目 tv-linux 的字幕实现
 */

import type { SubtitleItem, SubtitleSize, SubtitleSizeConfig, SubtitlePosition, SubtitleStyle } from '@/types/subtitle';

/**
 * 字幕大小配置
 * 参考 tv-linux/src/util/const.js
 */
export const SUBTITLE_SIZE_CONFIG: Record<SubtitleSize, SubtitleSizeConfig> = {
  small: {
    id: 0,
    name: 'small',
    fontSize: 19.6,
    renderFontSize: 38.5,
    size: 0.7,
  },
  middle: {
    id: 1,
    name: 'middle',
    fontSize: 25.2,
    renderFontSize: 49.5,
    size: 0.9,
  },
  large: {
    id: 2,
    name: 'large',
    fontSize: 30.8,
    renderFontSize: 60.5,
    size: 1.1,
  },
  larger: {
    id: 3,
    name: 'larger',
    fontSize: 36.4,
    renderFontSize: 71.5,
    size: 1.3,
  },
  largest: {
    id: 4,
    name: 'largest',
    fontSize: 39.2,
    renderFontSize: 77,
    size: 1.4,
  },
};

/**
 * 字幕样式基础配置
 */
export const SUBTITLE_STYLE_BASE = {
  lineHeight: 70,
  fontWeight: 450,
  textShadow: '1px 1px #000',
  backgroundColor: 'transparent', // 移除字幕背景
  background: 'transparent', // 兼容不同播放器API
  fontFamily:
    // 优先使用系统通用字体，确保在电视系统上正常显示
    // 将系统字体放在最前面，确保字体回退机制正确工作，避免显示"豆腐块"
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, 'Noto Sans KR', 'Noto Sans Devanagari', 'Noto Sans SC', 'Noto Sans TC', 'Noto Sans JP', 'Noto Sans Arabic', 'Noto Sans Thai', sans-serif", // 支持英语、阿拉伯语、印地语、泰语、韩语、简中、繁中、日语等多语言字幕
};

/**
 * LocalStorage 键名
 */
export const SUBTITLE_STORAGE_KEYS = {
  LANGUAGE: 'currentLanguage',
  FONTSIZE: 'currentFontsize',
  SETUP_LANGUAGE: 'setUpCurrentLanguage',
};

/**
 * 创建"无字幕"选项
 * 参考 tv-linux/src/views/newParticular/newParticular.js:5021-5036
 */
export function createNoSubtitleItem(): SubtitleItem {
  return {
    id: 0,
    name: 'No Subtitle',
    lang: 'No',
    langId: 0,
    url: '',
    filename: '',
    keyid: '',
    urlList: {
      ui: [{ url: '' }, { url: '' }],
    },
  };
}

/**
 * 处理字幕URL（将 .vtt.m3u8 转换为 .vtt）
 * 参考 tv-linux/src/views/newParticular/newParticular.js:3091
 */
export function processSubtitleUrl(url: string): string {
  if (!url) return '';
  return url.replace(/\.vtt\.m3u8/gi, '.vtt');
}

/**
 * 检查是否是VTT格式
 */
export function isVTTFormat(url: string): boolean {
  return /\.vtt/.test(url);
}

/**
 * 过滤字幕列表（用于不同渠道）
 * 参考 tv-linux/src/views/newParticular/newParticular.js:5040-5051
 *
 * @param subtitleList 原始字幕列表
 * @param channelId 渠道ID
 * @returns 过滤后的字幕列表
 */
export function filterSubtitlesByChannel(subtitleList: SubtitleItem[], channelId?: string): SubtitleItem[] {
  // Astro 渠道（16614）仅支持特定语言
  if (channelId === '16614') {
    const allowedLanguages = ['EN', 'ZH-TW', 'ZH-CN', 'MS', 'TH', 'No'];
    return subtitleList.filter((item) => allowedLanguages.includes(item.lang) || item.lang === 'No');
  }

  // 其他渠道返回完整列表
  return subtitleList;
}

/**
 * 获取字幕索引
 * 参考 tv-linux/src/util/util.js getSubtitleIndex 函数
 *
 * @param subtitleList 字幕列表
 * @param currentLanguage 当前语言
 * @param setupLanguage 设置中的语言
 * @returns 字幕索引
 */
export function getSubtitleIndex(
  subtitleList: SubtitleItem[],
  currentLanguage?: string | null,
  setupLanguage?: string | null
): number {
  if (!subtitleList || subtitleList.length === 0) {
    return 0;
  }

  // 优先使用当前语言
  if (currentLanguage) {
    const index = subtitleList.findIndex((item) => item.lang === currentLanguage);
    if (index !== -1) return index;
  }

  // 其次使用设置中的语言
  if (setupLanguage) {
    const index = subtitleList.findIndex((item) => item.lang === setupLanguage);
    if (index !== -1) return index;
  }

  // 默认返回第一个有URL的字幕，或返回0
  const firstSubtitle = subtitleList.findIndex((item) => item.url && item.url !== '');
  return firstSubtitle !== -1 ? firstSubtitle : 0;
}

/**
 * 计算字幕位置
 * 参考 tv-linux/src/views/newParticular/newParticular.js:3093-3111
 *
 * @param videoWidth 视频原始宽度
 * @param videoHeight 视频原始高度
 * @param containerWidth 容器宽度
 * @param containerHeight 容器高度
 * @param captionTopHPercent 字幕顶部百分比（默认90）
 * @returns 字幕位置配置
 */
export function calculateSubtitlePosition(
  videoWidth: number,
  videoHeight: number,
  containerWidth: number,
  containerHeight: number,
  captionTopHPercent = 90
): SubtitlePosition {
  // 计算视频缩放比例（取较小值以保持宽高比）
  const scaleRate = Math.min(containerWidth / videoWidth, containerHeight / videoHeight);

  // 计算实际显示的视频高度
  const realHeight = videoHeight * scaleRate;

  // 计算上下空白区域的高度
  const blankHeight = (containerHeight - realHeight) / 2;

  // 计算字幕底部位置
  // 字幕位置 = 实际视频高度 * (1 - 顶部百分比) + 空白区域 + 额外偏移
  const subtitleBottom = realHeight * (1 - captionTopHPercent / 100) + blankHeight + 20;

  return {
    x: 'middle',
    y: 'max',
    offsetY: -1 * subtitleBottom,
  };
}

/**
 * 创建字幕样式配置
 *
 * @param size 字幕大小
 * @returns 字幕样式配置
 */
export function createSubtitleStyle(size: SubtitleSize): SubtitleStyle {
  const sizeConfig = SUBTITLE_SIZE_CONFIG[size];

  return {
    textTrack: {
      fontSize: sizeConfig.renderFontSize,
      ...SUBTITLE_STYLE_BASE,
    },
  };
}

/**
 * 从LocalStorage加载字幕偏好
 *
 * @returns 字幕偏好配置
 */
export function loadSubtitlePreferences(): {
  language: string | null;
  fontSize: string | null;
  setupLanguage: string | null;
} {
  if (typeof window === 'undefined') {
    return {
      language: null,
      fontSize: null,
      setupLanguage: null,
    };
  }

  return {
    language: localStorage.getItem(SUBTITLE_STORAGE_KEYS.LANGUAGE),
    fontSize: localStorage.getItem(SUBTITLE_STORAGE_KEYS.FONTSIZE),
    setupLanguage: localStorage.getItem(SUBTITLE_STORAGE_KEYS.SETUP_LANGUAGE),
  };
}

/**
 * 保存字幕语言偏好到LocalStorage
 *
 * @param language 语言代码
 */
export function saveSubtitleLanguagePreference(language: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(SUBTITLE_STORAGE_KEYS.LANGUAGE, language);
}

/**
 * 保存字幕大小偏好到LocalStorage
 *
 * @param fontSize 字号大小
 */
export function saveSubtitleFontsizePreference(fontSize: number): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(SUBTITLE_STORAGE_KEYS.FONTSIZE, fontSize.toString());
}

/**
 * 获取字幕大小配置
 *
 * @param size 字幕大小
 * @returns 字幕大小配置
 */
export function getSubtitleSizeConfig(size: SubtitleSize): SubtitleSizeConfig {
  return SUBTITLE_SIZE_CONFIG[size];
}

/**
 * 获取所有字幕大小选项
 *
 * @returns 字幕大小选项数组
 */
export function getAllSubtitleSizeOptions(): SubtitleSizeConfig[] {
  return Object.values(SUBTITLE_SIZE_CONFIG);
}

/**
 * 根据字号查找字幕大小
 *
 * @param fontSize 字号
 * @returns 字幕大小类型
 */
export function findSubtitleSizeByFontSize(fontSize: number): SubtitleSize {
  const entry = Object.entries(SUBTITLE_SIZE_CONFIG).find(([, config]) => config.renderFontSize === fontSize);

  return (entry?.[0] as SubtitleSize) || 'middle';
}
